import axios from "axios";
import { getApiRoute } from "src/services";
import { getEventsFromRecentEvents } from "src/utils/Utils";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

const CALENDARS_BY_TAGS_STALE_TIME_MS = 1000 * 60 * 60;

async function resolveCalendarsWithRecentEvents(calendars) {
  const calendarsResolved = [];
  const setFn = (subcomponents) => {
    const calendarsIdResolved = new Set(
      calendarsResolved.map((calendar) => calendar.calendarId)
    );
    for (let i = 0; i < subcomponents.length; i++) {
      if (!calendarsIdResolved.has(subcomponents[i].calendarId)) {
        calendarsResolved.push(subcomponents[i]);
        calendarsIdResolved.add(subcomponents[i].calendarId);
      }
    }
  };
  await getEventsFromRecentEvents(calendars, setFn);
  return calendarsResolved;
}

function resolveRelatedTags(uniqueTags, searchTagsSet) {
  const relatedTags = [];
  const addedTags = new Set();
  for (const tag of uniqueTags) {
    if (typeof tag !== "string" || !tag.trim()) continue;
    const lowerTag = tag.toLowerCase();
    if (!searchTagsSet.has(lowerTag) && !addedTags.has(lowerTag)) {
      relatedTags.push(tag);
      addedTags.add(lowerTag);
    }
  }
  return relatedTags;
}

export const leaderboardByTagsQueryKey = (tags = [], matchAll = false) => [
  "leaderboard-by-tags",
  tags,
  matchAll,
];

export async function leaderboardByTags(tags, matchAll = false) {
  // Anonymous GET (no credentials) so the response stays shared-cacheable.
  const response = await axios.get(getApiRoute("home", "GET_LEADERBOARD_BY_TAGS"), {
    params: { tags: tags.join(","), ...(matchAll ? { matchAll: true } : {}) },
  });
  let leaderboardCalendars = [];
  if (response?.data?.leaderboard && Array.isArray(response.data.leaderboard)) {
    leaderboardCalendars = await resolveCalendarsWithRecentEvents(
      response.data.leaderboard
    );
  }
  return leaderboardCalendars;
}

export const leaderboardByTagsQueryOptions = (tags = [], matchAll = false) => ({
  queryKey: leaderboardByTagsQueryKey(tags, matchAll),
  queryFn: () => leaderboardByTags(tags, matchAll),
  staleTime: 1000 * 60 * 30,
});

/**
 * Leaderboard-only fetch (top calendars by subscribers across the given tags),
 * without the A-Z pages. For surfaces that want just the "Most Popular" rail —
 * e.g. the home major-league leaderboard. Cached for the session since the
 * ranking moves slowly.
 *
 * @param {string[]} tags
 * @param {object} [options] - react-query overrides.
 */
export const useLeaderboardByTags = (tags = [], options = {}) =>
  useQuery(leaderboardByTagsQueryKey(tags), () => leaderboardByTags(tags), {
    enabled: Array.isArray(tags) && tags.length > 0,
    staleTime: leaderboardByTagsQueryOptions(tags).staleTime,
    ...options,
  });

export async function calendarsByTags({ tags = [], page, pageSize, matchAll = false } = {}) {
  // Anonymous GET (no credentials) so the response stays shared-cacheable.
  const response = await axios.get(getApiRoute("home", "GET_CALENDARS_BY_TAGS"), {
    params: { tags: tags.join(","), page, pageSize, ...(matchAll ? { matchAll: true } : {}) },
  });
  const subscriberAZCalendars = await resolveCalendarsWithRecentEvents(
    response.data.calendarsWithSubscribers
  );
  return { ...response.data, subscriberAZCalendars };
}

export const useCalendarsByTags = (
  params = { tags: [], pageSize: 50 },
  options = {}
) => {
  const query = useInfiniteQuery(
    ["calendars-by-tags", params.tags, params.pageSize, Boolean(params.matchAll)],
    async ({ pageParam = 1 }) => {
      const isFirstPage = pageParam === 1;
      const calendarsPromise = calendarsByTags({ ...params, page: pageParam });
      const leaderboardPromise = isFirstPage
        ? leaderboardByTags(params.tags, params.matchAll)
        : Promise.resolve([]);

      const [data, leaderboardCalendars] = await Promise.all([
        calendarsPromise,
        leaderboardPromise,
      ]);
      return { ...data, leaderboardCalendars: [...leaderboardCalendars] };
    },
    {
      staleTime: CALENDARS_BY_TAGS_STALE_TIME_MS,
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage, allPages) => {
        //Iif the last page doesn't has pageSize length, there are no more pages
        if (lastPage.calendarsWithSubscribers.length !== params.pageSize) {
          return undefined;
        }
        // Return the next page number
        return allPages.length + 1;
      },
      enabled: !!params.tags && params.tags.length > 0, // Only execute if there are tags
      ...options,
    }
  );

  const subscriberAZCalendars = useMemo(() => {
    if (!query.data) return [];
    return query.data.pages.flatMap((page) => page.subscriberAZCalendars);
  }, [query.data]);

  // Accumulate all unique tags from pages, removing case-insensitive duplicates
  const allUniqueTags = useMemo(() => {
    if (!query.data) return [];
    const allTags = query.data.pages.flatMap((page) => page.uniqueTags);
    const seen = new Set();
    const result = [];
    allTags.forEach((tag) => {
      if (typeof tag !== "string" || !tag.trim()) return;
      const lower = tag.toLowerCase();
      if (!seen.has(lower)) {
        seen.add(lower);
        result.push(tag);
      }
    });
    return result;
  }, [query.data]);

  // Calculate relatedTags when allUniqueTags or search tags change
  const relatedTags = useMemo(() => {
    const searchTagsSet = new Set(params.tags.map((t) => t.toLowerCase()));
    return resolveRelatedTags(allUniqueTags, searchTagsSet);
  }, [allUniqueTags, params.tags]);

  return {
    ...query,
    data: {
      subscriberAZCalendars,
      totalCalendars:
        query.data?.pages[query.data.pages.length - 1].totalCalendars,
      leaderboardCalendars: query.data?.pages?.[0]?.leaderboardCalendars,
      relatedTags,
      hasMoreSearchByTagsPages: query.hasNextPage,
    },
  };
};
